/**
 * The course line, read from the organiser's GPX file.
 *
 * Only ever consulted as a FALLBACK: EventCategories draws `route_polyline`
 * when the organiser stored one, and asks for this only when they did not.
 *
 * Fetched once per URL and cached for the life of the page: several activities
 * can share a course, and a re-render must not re-download it.
 *
 * Failure is silent by design. A course file that 404s, is served from a host
 * that refuses the read, or contains no track leaves the page exactly as it is
 * without it — the addresses and the two pins still describe the route. An
 * error banner about a file the visitor did not ask for would be noise.
 */

import { useEffect, useState } from 'react';
import parseGpx from './parseGpx';

// url -> parsed line (or null for "tried, nothing usable came back")
const cache = new Map();

// url -> in-flight promise.
//
// WHY THIS EXISTS. The organiser form takes a GPX two ways: an upload, which
// lands on our own file server, or a link to one "already online — Garmin,
// Strava or your own site". Both end up in the same column, so this hook is
// pointed at whatever they chose.
//
// A cross-origin host that sends no Access-Control-Allow-Origin (Garmin answers
// 302 to a login page, so it never could) fails the fetch, and the cache above
// is only written once the fetch settles. StrictMode runs the effect twice
// immediately, so both passes missed the cache and both hit the network:
// two requests per activity per page load, for a line that may never draw.
//
// Sharing the in-flight promise makes it one request per URL per page load
// whatever the outcome, WITHOUT deciding in advance which hosts are allowed to
// answer. That distinction matters: a GPX on the organiser's own CORS-enabled
// site is exactly the case the form invites, and refusing to fetch it on the
// grounds that it is cross-origin would break the feature to save a request.
//
// A host that genuinely refuses still costs one failed request per page load.
// Removing that last one needs the file fetched server-side and re-served from
// our origin — there is no client-only fix, because the refusal comes from the
// other host.
const inFlight = new Map();

function loadGpx(url) {
  if (cache.has(url)) return Promise.resolve(cache.get(url));
  const pending = inFlight.get(url);
  if (pending) return pending;

  const promise = fetch(url)
    .then(resp => (resp.ok ? resp.text() : ''))
    .then(text => parseGpx(text))
    .catch(() => null)
    .then(line => {
      cache.set(url, line);
      inFlight.delete(url);
      return line;
    });

  inFlight.set(url, promise);
  return promise;
}

export default function useGpxRoute(url) {
  const key = typeof url === 'string' ? url : '';
  const [line, setLine] = useState(() => cache.get(key) || null);

  useEffect(() => {
    if (!key) { setLine(null); return undefined; }
    if (cache.has(key)) { setLine(cache.get(key)); return undefined; }

    let alive = true;
    // No AbortController: the request is shared with any other activity on the
    // page pointing at the same course, so one unmounting must not cancel it
    // for the others. Unmounting simply stops this copy from setting state.
    loadGpx(key).then(parsed => { if (alive) setLine(parsed); });

    return () => { alive = false; };
  }, [key]);

  return line;
}

/** Test seam: forget what has been fetched. */
export function clearGpxCache() {
  cache.clear();
  inFlight.clear();
}
